import { Immutable } from "immer";
import React from "react";

/**
 * A group of pre-serialized dataLayer events that share one delivery-dedup
 * identity (e.g. `ga_ecom:100423`). A bundle is pushed at most once per
 * browser: the key becomes a localStorage marker, so a re-render of the same
 * order on a later page (NPS survey then thank-you, or a reload) is a no-op.
 */
export type IDataLayerEventBundle = Immutable<{
    key: string;
    events: readonly string[];
}>;

type IProps = Immutable<{
    events: readonly (string | IDataLayerEventBundle)[];
}>;

const MARKER_PREFIX = "ga:fired:";
const MAX_MARKERS = 50;

const toBundles = (
    events: Immutable<readonly (string | IDataLayerEventBundle)[]>,
): IDataLayerEventBundle[] =>
    events.map((entry) =>
        typeof entry === "string" ? { key: "", events: [entry] } : entry,
    );

/**
 * JUST the JS -- the `<script>` wrapper is supplied by the caller. Exported for
 * testing; `DataLayerPush` is the only production caller.
 *
 * XSS-safety invariant: each event is a JSON string that `_push_to_datalayer`
 * (tsicommon_tracker_tags) already emitted with `<`, `>` and `&` replaced by
 * their `\uXXXX` escapes, so order data can never close the script tag. We
 * re-apply the same escaping to the serialized literal here so the guarantee
 * does not depend on every producer honoring it. The escapes decode back to the
 * original characters when the browser parses the string literal, and each
 * element is then `JSON.parse`d at runtime -- the double encoding round-trips.
 *
 * The push loop swallows its own failures so one malformed bundle cannot stop
 * the rest, but it is never silent: a payload regression here would otherwise
 * be invisible until someone noticed the revenue dip, which is how #35199 ran
 * for weeks.
 *
 * `prune` runs BEFORE the write, never after: a full origin makes `setItem`
 * throw, and a prune sitting after it would never reach the space it needed to
 * free -- the marker would be lost and its bundle would re-fire on every later
 * render. The retry drops every marker we own, since over-counting one order is
 * the failure direction this design already accepts.
 *
 * ES5-only (var/function, no arrow or optional chaining) and self-contained:
 * this text is inlined into the SSR HTML and must run in whatever the visitor
 * brought, before any bundle loads.
 */
export const dataLayerPushScript = (
    events: Immutable<readonly (string | IDataLayerEventBundle)[]>,
): string => {
    const literal = JSON.stringify(toBundles(events))
        .replace(/</g, "\\u003c")
        .replace(/>/g, "\\u003e")
        .replace(/&/g, "\\u0026")
        .replace(/\u2028/g, "\\u2028")
        .replace(/\u2029/g, "\\u2029");
    return `
(function(w){
var P=${JSON.stringify(MARKER_PREFIX)},M=${MAX_MARKERS},b=${literal};
w.dataLayer=w.dataLayer||[];
function fired(k){try{return w.localStorage.getItem(P+k)!==null;}catch(e){return false;}}
function prune(m){
var s=w.localStorage,keys=[],i,k;
for(i=0;i<s.length;i++){k=s.key(i);if(k&&k.indexOf(P)===0){keys.push([k,parseInt(s.getItem(k),10)||0]);}}
if(keys.length<=m){return;}
keys.sort(function(a,c){return a[1]-c[1];});
for(i=0;i<keys.length-m;i++){s.removeItem(keys[i][0]);}
}
function mark(k){var v=String(new Date().getTime());try{prune(M-1);w.localStorage.setItem(P+k,v);}catch(e){try{prune(0);w.localStorage.setItem(P+k,v);}catch(e2){}}}
for(var i=0;i<b.length;i++){
var key=b[i].key,evts=b[i].events;
if(!evts.length){continue;}
if(key&&fired(key)){continue;}
try{
for(var j=0;j<evts.length;j++){w.dataLayer.push(JSON.parse(evts[j]));}
if(key){mark(key);}
}catch(e){if(w.console&&w.console.error){w.console.error("dataLayer push failed",key,e);}}
}
})(window);
`;
};

/**
 * SSR-ONLY. Deliver pre-serialized GA4 dataLayer events through an inline
 * `<script>` that runs while the browser parses the document -- not from an
 * effect after hydration. Purchase events are revenue reporting: delivery must
 * not depend on hydration succeeding, so the server renders them on every
 * request and dedup happens here, at delivery time. A bundle carrying a `key`
 * is pushed only if its localStorage marker is absent, and the marker is
 * written AFTER the push. Every storage access is fail-open -- a browser that
 * denies localStorage over-counts rather than losing the order.
 *
 * Rendering this anywhere but the initial SSR payload delivers nothing: on a
 * client-only mount (an island, a conditional that first mounts after
 * hydration, an SPA route transition) React builds the `<script>` through
 * `innerHTML`, which the browser never executes, and every event is dropped
 * with no exception, no warning and no failing type.
 *
 * React does not re-execute an inline script when it hydrates against matching
 * SSR markup, so the script body runs exactly once per document regardless of
 * StrictMode double-invocation; the marker covers the remaining cases.
 */
export const DataLayerPush = (props: IProps): React.ReactNode => {
    if (props.events.length === 0) {
        return null;
    }
    return (
        <script
            dangerouslySetInnerHTML={{
                __html: dataLayerPushScript(props.events),
            }}
        />
    );
};
